import { useEffect, useState } from 'react';
import { DonViCoSo, DonViCoSoInput, DonViCoSoResponse } from '@/types/schemas/don-vi-co-so.type';
import { useAuth } from '@/contexts/auth-context';
import QUERY_KEYS from '@/constants/query-keys';
import api from '@/lib/api';

interface UseDonViCoSoReturn {
  donViCoSos: DonViCoSo[];
  isLoading: boolean;
  addDonViCoSo: (newDonViCoSo: DonViCoSoInput) => Promise<DonViCoSo>;
  updateDonViCoSo: (uuid: string, updatedDonViCoSo: DonViCoSoInput) => Promise<DonViCoSo>;
  deleteDonViCoSo: (uuid: string) => Promise<void>;
  refreshDonViCoSos: () => Promise<void>;
}

/**
 * Hook for managing DonViCoSo (Base Unit) data
 *
 * This hook provides functions to fetch, create, update, and delete base organizational units.
 */
export const useDonViCoSo = (initialDonViCoSos: DonViCoSo[] = []): UseDonViCoSoReturn => {
  const [donViCoSos, setDonViCoSos] = useState<DonViCoSo[]>(initialDonViCoSos);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchDonViCoSos = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<DonViCoSoResponse>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_VI}/`);
      setDonViCoSos(response.data.results);
    } catch (error) {
      // Error handling
    } finally {
      setIsLoading(false);
    }
  };

  const addDonViCoSo = async (newDonViCoSo: DonViCoSoInput): Promise<DonViCoSo> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        name: newDonViCoSo.name,
        document_prefix: newDonViCoSo.document_prefix,
        active: newDonViCoSo.active ?? true,
        hidden: newDonViCoSo.hidden ?? false
      };

      const response = await api.post<DonViCoSo>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_VI}/`, payload);
      const addedDonViCoSo = response.data;

      setDonViCoSos(prev => [...prev, addedDonViCoSo]);
      return addedDonViCoSo;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDonViCoSo = async (uuid: string, updatedDonViCoSo: DonViCoSoInput): Promise<DonViCoSo> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      const payload = {
        name: updatedDonViCoSo.name,
        document_prefix: updatedDonViCoSo.document_prefix,
        active: updatedDonViCoSo.active ?? true,
        hidden: updatedDonViCoSo.hidden ?? false
      };

      const response = await api.put<DonViCoSo>(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_VI}/${uuid}/`, payload);
      const updatedDonViCoSoData = response.data;

      setDonViCoSos(prev =>
        prev.map(donViCoSo => (donViCoSo.uuid === updatedDonViCoSoData.uuid ? updatedDonViCoSoData : donViCoSo))
      );

      return updatedDonViCoSoData;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteDonViCoSo = async (uuid: string): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.DON_VI}/${uuid}/`);
      setDonViCoSos(prev => prev.filter(donViCoSo => donViCoSo.uuid !== uuid));
    } finally {
      setIsLoading(false);
    }
  };

  const refreshDonViCoSos = async (): Promise<void> => {
    await fetchDonViCoSos();
  };

  useEffect(() => {
    fetchDonViCoSos();
  }, [entity?.slug]);

  return {
    donViCoSos,
    isLoading,
    addDonViCoSo,
    updateDonViCoSo,
    deleteDonViCoSo,
    refreshDonViCoSos
  };
};
